import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  MoreVert as MoreIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
} from '@mui/icons-material';

const Referrals = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [programOpen, setProgramOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('All');

  const referralPrograms = [
    {
      id: 1,
      name: 'Customer Referral Program',
      description: 'Earn $50 for every successful referral',
      reward: '$50 credit',
      status: 'Active',
      participants: 156,
      totalReferrals: 89,
      successfulReferrals: 34,
      totalRewards: 1700,
      createdDate: '2023-08-15',
    },
    {
      id: 2,
      name: 'VIP Referral Program',
      description: 'Premium customers earn $100 for referrals',
      reward: '$100 credit',
      status: 'Active',
      participants: 45,
      totalReferrals: 67,
      successfulReferrals: 28,
      totalRewards: 2800,
      createdDate: '2023-10-01',
    },
  ];

  const referrers = [
    {
      id: 'REF-001',
      name: 'John Smith',
      email: 'john.smith@email.com',
      tier: 'Gold',
      totalReferrals: 12,
      successfulReferrals: 8,
      totalEarned: 400,
      pendingRewards: 100,
      joinDate: '2023-09-15',
      lastReferral: '2024-01-18',
      status: 'Active',
      referralCode: 'JOHNS2024',
      recentReferrals: [
        { name: 'Alice Johnson', date: '2024-01-18', status: 'Successful', reward: 50 },
        { name: 'Bob Wilson', date: '2024-01-15', status: 'Pending', reward: 50 },
        { name: 'Carol Davis', date: '2024-01-10', status: 'Successful', reward: 50 },
      ],
    },
    {
      id: 'REF-002',
      name: 'Sarah Johnson',
      email: 'sarah@example.com',
      tier: 'Silver',
      totalReferrals: 8,
      successfulReferrals: 5,
      totalEarned: 250,
      pendingRewards: 50,
      joinDate: '2023-10-20',
      lastReferral: '2024-01-16',
      status: 'Active',
      referralCode: 'SARAHJ2024',
      recentReferrals: [
        { name: 'David Brown', date: '2024-01-16', status: 'Successful', reward: 50 },
        { name: 'Emma Taylor', date: '2024-01-12', status: 'Pending', reward: 50 },
      ],
    },
    {
      id: 'REF-003',
      name: 'Mike Wilson',
      email: 'mike.wilson@company.com',
      tier: 'Bronze',
      totalReferrals: 4,
      successfulReferrals: 3,
      totalEarned: 150,
      pendingRewards: 0,
      joinDate: '2023-11-05',
      lastReferral: '2024-01-08',
      status: 'Active',
      referralCode: 'MIKEW2024',
      recentReferrals: [
        { name: 'Frank Miller', date: '2024-01-08', status: 'Successful', reward: 50 },
        { name: 'Grace Anderson', date: '2023-12-20', status: 'Successful', reward: 50 },
      ],
    },
  ];

  const stats = [
    {
      title: 'Total Referrers',
      value: referrers.length.toString(),
      icon: <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      change: '+12%',
    },
    {
      title: 'Total Referrals',
      value: referrers.reduce((acc, r) => acc + r.totalReferrals, 0).toString(),
      icon: <GroupIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      change: '+25%',
    },
    {
      title: 'Successful Referrals',
      value: referrers.reduce((acc, r) => acc + r.successfulReferrals, 0).toString(),
      icon: <StarIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      change: '+18%',
    },
    {
      title: 'Total Rewards Paid',
      value: `$${referrers.reduce((acc, r) => acc + r.totalEarned, 0).toLocaleString()}`,
      icon: <MoneyIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      change: '+32%',
    },
  ];

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Gold':
        return 'warning';
      case 'Silver':
        return 'info';
      case 'Bronze':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Successful':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredReferrers = referrers.filter((referrer) => {
    const matchesSearch = referrer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         referrer.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || referrer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDetailsOpen = (referrer) => {
    setSelectedReferrer(referrer);
    setDetailsOpen(true);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, referrer) => {
    setAnchorEl(event.currentTarget);
    setSelectedReferrer(referrer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReferrer(null);
  };

  const copyReferralCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Referral Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setProgramOpen(true)}
          >
            New Program
          </Button>
          <Button
            variant="contained"
            startIcon={<ShareIcon />}
            sx={{ fontWeight: 'bold' }}
          >
            Share Program
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  {stat.icon}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon color="success" fontSize="small" />
                    <Typography
                      variant="body2"
                      sx={{ color: 'success.main', fontWeight: 'bold', ml: 0.5 }}
                    >
                      {stat.change}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Referral Programs */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            Active Referral Programs
          </Typography>
          <Grid container spacing={3}>
            {referralPrograms.map((program) => (
              <Grid item xs={12} md={6} key={program.id}>
                <Paper variant="outlined" sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {program.name}
                    </Typography>
                    <Chip
                      label={program.status}
                      color={program.status === 'Active' ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {program.description}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Reward: {program.reward}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Participants</Typography>
                      <Typography variant="h6">{program.participants}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                      <Typography variant="h6">
                        {Math.round((program.successfulReferrals / program.totalReferrals) * 100)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Total Referrals</Typography>
                      <Typography variant="body1">{program.totalReferrals}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Rewards Paid</Typography>
                      <Typography variant="body1">${program.totalRewards.toLocaleString()}</Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(program.successfulReferrals / program.totalReferrals) * 100}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Referrers Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Referral Partners
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search referrers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referrer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tier</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referrals</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Success Rate</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Earned</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Pending</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referral Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReferrers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((referrer) => (
                    <TableRow key={referrer.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, backgroundColor: 'primary.main' }}>
                            {referrer.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {referrer.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {referrer.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={referrer.tier}
                          color={getTierColor(referrer.tier)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {referrer.successfulReferrals}/{referrer.totalReferrals}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {Math.round((referrer.successfulReferrals / referrer.totalReferrals) * 100)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          ${referrer.totalEarned}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="warning.main">
                          ${referrer.pendingRewards}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', mr: 1 }}>
                            {referrer.referralCode}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => copyReferralCode(referrer.referralCode)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, referrer)}
                          size="small"
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredReferrers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDetailsOpen(selectedReferrer)}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem>
          <EditIcon sx={{ mr: 1 }} />
          Edit Referrer
        </MenuItem>
        <MenuItem>
          <EmailIcon sx={{ mr: 1 }} />
          Send Email
        </MenuItem>
        <MenuItem>
          <MoneyIcon sx={{ mr: 1 }} />
          Process Payout
        </MenuItem>
      </Menu>

      {/* Referrer Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Referrer Details - {selectedReferrer?.name}</DialogTitle>
        <DialogContent>
          {selectedReferrer && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Overview
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Referrer ID</Typography>
                  <Typography>{selectedReferrer.id}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography>{selectedReferrer.email}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Tier</Typography>
                  <Chip
                    label={selectedReferrer.tier}
                    color={getTierColor(selectedReferrer.tier)}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Join Date</Typography>
                  <Typography>{selectedReferrer.joinDate}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Referral Code</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontFamily: 'monospace', mr: 1 }}>
                      {selectedReferrer.referralCode}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyReferralCode(selectedReferrer.referralCode)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Performance
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Total Referrals</Typography>
                  <Typography sx={{ fontWeight: 'bold' }}>{selectedReferrer.totalReferrals}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Successful Referrals</Typography>
                  <Typography sx={{ fontWeight: 'bold' }}>{selectedReferrer.successfulReferrals}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {Math.round((selectedReferrer.successfulReferrals / selectedReferrer.totalReferrals) * 100)}%
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Total Earned</Typography>
                  <Typography sx={{ fontWeight: 'bold' }}>${selectedReferrer.totalEarned}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Pending Rewards</Typography>
                  <Typography sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    ${selectedReferrer.pendingRewards}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Last Referral</Typography>
                  <Typography>{selectedReferrer.lastReferral}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Recent Referrals
                </Typography>
                <List>
                  {selectedReferrer.recentReferrals.map((referral, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={referral.name}
                          secondary={`${referral.date} - $${referral.reward} reward`}
                        />
                        <Chip
                          label={referral.status}
                          color={getStatusColor(referral.status)}
                          size="small"
                        />
                      </ListItem>
                      {index < selectedReferrer.recentReferrals.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="outlined">Process Payout</Button>
          <Button variant="contained">Edit Referrer</Button>
        </DialogActions>
      </Dialog>

      {/* Create Program Dialog */}
      <Dialog open={programOpen} onClose={() => setProgramOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Referral Program</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Program Name"
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Reward Amount"
            margin="normal"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Reward Type</InputLabel>
            <Select label="Reward Type">
              <MenuItem value="credit">Store Credit</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="discount">Discount</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgramOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Program</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Referrals;
